import React, { useEffect, useState } from 'react';
import {
    ActivityIndicator,
    Alert,
    Dimensions,
    Platform,
    ScrollView,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import { BottomNavigation } from '../components/BottomNavigation';
import { CouponDetails } from '../components/CouponDetails';
import { FavouriteToggleButton } from '../components/FavouriteToggleButton';
import { ProductAtGlance } from '../components/ProductAtGlance';
import { ProductBarrierInfo } from '../components/ProductBarrierInfo';
import { ProductCouponsTable } from '../components/ProductCouponsTable';
import { ProductHeader } from '../components/ProductHeader';
import { ProductPerformanceChart } from '../components/ProductPerformanceChart';
import { ProductShareModal } from '../components/ProductShareModal';
import { ProductUnderlyingsInfo } from '../components/ProductUnderlyingsInfo';
import { useProductConfig } from '../config/productTypes/useProductConfig';
import { COLORS } from '../constants/theme';
import { apiService } from '../services/apiService';
import authService from '../services/authService';
import { exportProductPdf } from '../services/productPdfService';
import { getShareLinkErrorMessage } from '../utils/shareLinkErrors';
import { isXl } from '../utils/responsiveStyles';
import { styles } from './ProductDetailScreen.styles';

const { width } = Dimensions.get('window');

interface ProductDetailScreenProps {
  isin: string;
  onBackPress: () => void;
  onNavigateToProducts?: () => void;
  onNavigateToHome?: () => void;
  ownershipScope?: 'group' | 'self';
  fromCustomReport?: boolean;
}

interface Underlying {
  name: string;
  isin: string;
  initialFixingLevel: number;
  strikeLevel: number;
  barrierLevel: number;
  couponTrigger: number | null;
  autocallTrigger: number | null;
  marketPrice: number;
  performance: number;
  strikeDistance: number;
  barrierDistance: number;
  autocallDistance: number | null;
  couponBarrierDistance: number | null;
  underlyingCurrency?: string;
  upperStrikePct: number | null;
}

interface Coupon {
  periodNumber: number;
  exDate: string;
  paymentDate: string;
  amount: string;
  isPaid: boolean;
  isAutocalled: boolean;
}

interface EarlyRedemptionItem {
  observationDate: string;
  redemptionLevel?: number;
  redemptionAmount?: string;
  wasTriggered?: boolean;
  periodNumber?: number;
}

interface HistoryPoint {
  date: string;
  price: number;
}

interface UnderlyingHistorySeries {
  name: string;
  history: HistoryPoint[];
}

interface ProductDetail {
  productId: number;
  name: string;
  isin: string;
  valor: string;
  currency: string;
  bid: number;
  ask: number;
  productType: string;
  productCode?: string;
  productTypeName?: string;
  issuePrice: number;
  tenorMonths: number;
  denomination: number;
  coupon: number;
  initialFixing: string;
  issueDate: string;
  finalFixing: string;
  redemptionDate: string;
  settlementType: number;
  settlementTypeName: string;
  issuer: string;
  barrierLevel: number;
  strikeLevel: number;
  barrierStyle: number;
  barrierStyleName: string;
  barrierHitRecorded?: boolean;
  productCallType: 'autocall' | 'callable' | 'not-callable';
  underlyings: Underlying[];
  coupons: Coupon[];
  couponBarrier: number | null;
  couponMemory: boolean | null;
  upside: number;
  upsideParticipation: number;
  downside: number;
  downsideParticipation: number;
  grade: string;
  recovery: string;
  couponFrequencyName: string;
  couponMinPct: number;
  couponMaxPct: number;
  couponTypeName: string;
  referenceRateName: string;
  premiumPct: number | null;
  quotationType: string | null;
  basketOptionTypeName: string | null;
  upperStrikePct: number | null;
  barrierUpper: number | null;
  leverage: number | null;
  ratio: number | null;
  stopLoss: number | null;
  couponTrigger: number | null;
  couponStepUp: number | null;
  capitalProtectionPct: number | null;
  stopLossDistance: number | null; 
  basketTypeUp: number;
  bonusLevel: number;
  capLevel: number;
  maxReturn: number;
  termSheet?: string | null;
  tradedNotional: number;
  currentNotional: number;
}

export const ProductDetailScreen: React.FC<ProductDetailScreenProps> = ({
  isin,
  onBackPress,
  onNavigateToProducts,
  onNavigateToHome,
  ownershipScope = 'group',
  fromCustomReport = false,
}) => {
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUnderlying, setSelectedUnderlying] = useState(0);
  const [activeTab, setActiveTab] = useState<'Week' | 'Month' | 'Year' | 'YTD' |'All'>('Year');
  const [priceHistory, setPriceHistory] = useState<Array<{ date: string; price: number }>>([]);
  const [fullPriceHistory, setFullPriceHistory] = useState<Array<{ date: string; price: number }>>(
    [],
  );
  const [underlyingsHistory, setUnderlyingsHistory] = useState<UnderlyingHistorySeries[]>([]);
  const [fullUnderlyingsHistory, setFullUnderlyingsHistory] = useState<UnderlyingHistorySeries[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [visibleUnderlyings, setVisibleUnderlyings] = useState<boolean[]>([]);
  const [earlyRedemption, setEarlyRedemption] = useState<EarlyRedemptionItem[]>([]);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const { shouldShow, sections } = useProductConfig(
    product ? { ...product, productName: product.name } : null
  );

  const showTradedNotional =
    !fromCustomReport && sections.atGlance.props.tradedNotional !== false;
  const showCurrentNotional =
    !fromCustomReport && sections.atGlance.props.currentNotional !== false;

  const [isFocused, setIsFocused] = useState(false);


  useEffect(() => {
    loadProductDetails();
  }, [isin, ownershipScope]);

  // Cargar earlyRedemption cuando cambie el producto y la configuración lo requiera
  useEffect(() => {
    if (product && shouldShow.earlyRedemption) {
      const loadData = async () => {
        try {
          const redemptionData = await apiService.getEarlyRedemption(product.isin, ownershipScope);
          setEarlyRedemption(redemptionData);
        } catch (error) {
          setEarlyRedemption([]);
        }
      };

      loadData();
    } else {
      setEarlyRedemption([]);
    }
  }, [product?.isin, shouldShow.earlyRedemption, ownershipScope]);

  useEffect(() => {
    if (product) {
      loadFullPriceHistory();
    }
  }, [isin, product, ownershipScope]);

  useEffect(() => {
    if (fullPriceHistory.length > 0 && !loadingHistory) {
      filterPriceHistoryByPeriod();
    } else if (fullPriceHistory.length === 0 && !loadingHistory) {
      setPriceHistory([]);
      setUnderlyingsHistory([]);
    }
  }, [activeTab, fullPriceHistory, fullUnderlyingsHistory, loadingHistory]);

  const loadProductDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getProductByIsin(isin, ownershipScope);
      
      const traded = data.tradedNotional ?? 0;
      const current =
        data.currentNotional != null && Number.isFinite(Number(data.currentNotional))
          ? Number(data.currentNotional)
          : traded;
      setProduct({ ...data, tradedNotional: traded, currentNotional: current });

      if (data.underlyings && data.underlyings.length > 0) {
        setSelectedUnderlying(0);
        setVisibleUnderlyings(new Array(data.underlyings.length).fill(true));
      }

      if (data.tenorMonths && data.tenorMonths > 12) {
        setActiveTab('All');
      } else {
        setActiveTab('Year');
      }

      // Early redemption se carga en un useEffect separado basado en shouldShow.earlyRedemption
      // if (data.productCallType === 'autocall' || data.productCallType === 'callable') {
      //   try {
      //     const redemptionData = await apiService.getEarlyRedemption(isin);
      //     setEarlyRedemption(redemptionData);
      //   } catch {
      //     setEarlyRedemption([]);
      //   }
      // }
    } catch (err: any) {
      setError(err.message || 'Error loading product details');
    } finally {
      setLoading(false);
    }
  };

  const loadFullPriceHistory = async () => {
    try {
      setLoadingHistory(true);
      const productHistory = await apiService.getProductPriceHistory(isin, 'all', ownershipScope);

      const sortedFullHistory = productHistory
        .map((item) => ({ date: item.date, price: item.price }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      setFullPriceHistory(sortedFullHistory);

      try {
        const underlyingsData = await apiService.getUnderlyingsPriceHistory(isin, 'all', ownershipScope);
        setFullUnderlyingsHistory(underlyingsData);
      } catch {
        setFullUnderlyingsHistory([]);
      }
    } catch {
      setFullPriceHistory([]);
      setFullUnderlyingsHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const filterPriceHistoryByPeriod = () => {
    if (fullPriceHistory.length === 0) {
      setPriceHistory([]);
      setUnderlyingsHistory([]);
      return;
    }

    const sortedFullHistory = [...fullPriceHistory].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );

    const mostRecentDataDate =
      sortedFullHistory.length > 0 ? new Date(sortedFullHistory[0].date) : new Date();

    const endDate = new Date(mostRecentDataDate);
    endDate.setHours(23, 59, 59, 999);

    let startDate: Date;

    switch (activeTab) {
      case 'Week':
        startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'Month':
        startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'Year':
        startDate = new Date(endDate.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      case 'YTD':
        const year = new Date().getFullYear(); 
        startDate = new Date(year, 0, 1); //primer dia del año en curso - 20331
        break;
      case 'All':
      default:
        setPriceHistory(fullPriceHistory);
        setUnderlyingsHistory(fullUnderlyingsHistory || []);
        return;
    }

    const filteredHistory = fullPriceHistory
      .filter((item) => {
        const itemDate = new Date(item.date);
        if (isNaN(itemDate.getTime())) return false;
        return itemDate.getTime() >= startDate.getTime() && itemDate.getTime() <= endDate.getTime();
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    setPriceHistory([...filteredHistory]);

    if (fullUnderlyingsHistory && fullUnderlyingsHistory.length > 0) {
      const filteredUnderlyings = fullUnderlyingsHistory.map((underlying) => {
        if (!underlying.history || underlying.history.length === 0) return underlying;

        const filteredUnderlyingHistory = underlying.history
          .filter((h) => {
            const itemDate = new Date(h.date);
            if (isNaN(itemDate.getTime())) return false;
            return (
              itemDate.getTime() >= startDate.getTime() && itemDate.getTime() <= endDate.getTime()
            );
          })
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        return { ...underlying, history: filteredUnderlyingHistory };
      });
      setUnderlyingsHistory(filteredUnderlyings);
    } else {
      setUnderlyingsHistory([]);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const toggleUnderlyingVisibility = (index: number) => {
    setVisibleUnderlyings((prev) => {
      const newVisibility = [...prev];
      newVisibility[index] = !newVisibility[index];
      return newVisibility;
    });
  };

  const calculatePerformance = () => {
    // En "All" se calcula desde emisión expresado en porcentaje
    // Fórmula requerida: ((currentPrice - issuePrice) / issuePrice) * 100
    if (activeTab === 'All') { 
      if (!product || product.bid == null) return '0.00';

      const currentPrice = Number(product.bid);
      const issuePrice = Number(product.issuePrice);

      if (!isFinite(currentPrice) || !isFinite(issuePrice) || issuePrice === 0) return '0.00';

      const perfSinceIssue = ((currentPrice - issuePrice) / issuePrice) * 100;
      return isNaN(perfSinceIssue) ? '0.00' : perfSinceIssue.toFixed(2);
    }

    // En YTD, si el producto fue emitido en el año actual, usamos issuePrice como base
    // para que coincida con el total performance (All) en esos casos.
    if (activeTab === 'YTD') {
      if (!product || product.bid == null) return '0.00';

      const issueDate = new Date(product.issueDate);
      const currentYear = new Date().getFullYear();
      const isIssuedThisYear =
        !isNaN(issueDate.getTime()) && issueDate.getFullYear() === currentYear;

      if (isIssuedThisYear) {
        const currentPrice = Number(product.bid);
        const issuePrice = Number(product.issuePrice);

        if (!isFinite(currentPrice) || !isFinite(issuePrice) || issuePrice === 0) return '0.00';

        const perfSinceIssue = ((currentPrice - issuePrice) / issuePrice) * 100;
        return isNaN(perfSinceIssue) ? '0.00' : perfSinceIssue.toFixed(2);
      }
    }

    // Si tenemos histórico filtrado para el periodo seleccionado, calculamos la performance del rango
    if (priceHistory && priceHistory.length > 0) {
      const first = Number(priceHistory[0].price);
      const last = Number(priceHistory[priceHistory.length - 1].price);

      if (!isFinite(first) || !isFinite(last) || first === 0) {
      } else {
        const perfRange = ((last / first) - 1) * 100;
        return isNaN(perfRange) ? '0.00' : perfRange.toFixed(2);
      }
    }

    if (!product || product.bid == null) return '0.00';

    const bid = Number(product.bid);
    if (!isFinite(bid)) return '0.00';

    const issuePrice = Number(product.issuePrice);
    if (!isFinite(issuePrice) || issuePrice === 0) return '0.00';

    const performance = bid - issuePrice;
    return isNaN(performance) ? '0.00' : performance.toFixed(2);
  };

  const showOnlyUnderlying = (index: number) => {
    if (!product?.underlyings?.length) return;
  
    setSelectedUnderlying(index);
  
    setVisibleUnderlyings((prev) => {
      const next = new Array(product.underlyings.length).fill(false);
      next[index] = true;
      return next;
    });
  };

  const showAllUnderlyings = () => {
    if (!product?.underlyings?.length) return;

    setIsFocused(false);
    setVisibleUnderlyings(new Array(product.underlyings.length).fill(true));
  };

  const handleExportPdf = async () => {
    if (!product || isExportingPdf) {
      return;
    }

    try {
      setIsExportingPdf(true);
      await authService.getUserProfile();
      const sessionUser = authService.getCurrentUser() as { companyName?: string | null } | null;
      const exporterCompanyName =
        sessionUser && typeof sessionUser.companyName === 'string' ? sessionUser.companyName : '';
      const filteredEarlyRedemption = earlyRedemption.filter((item) => {
        if (sections.earlyRedemption.props.redemptionLevel) {
          return item.redemptionLevel != null;
        }
        return true;
      });
      // El PDF siempre debe exportar el chart con rango completo ("All").
      const pdfPriceHistory = fullPriceHistory.length > 0 ? fullPriceHistory : priceHistory;
      const pdfUnderlyingsHistory =
        fullUnderlyingsHistory.length > 0 ? fullUnderlyingsHistory : underlyingsHistory;

      await exportProductPdf({
        product: {
          ...product,
          productType: product.productTypeName || product.productType,
          couponFrequency: product.couponFrequencyName,
          couponType: product.couponTypeName,
          referenceRate: product.referenceRateName,
        },
        exporterCompanyName,
        sections: {
          atGlance: shouldShow.atGlance,
          barrier: shouldShow.barrier,
          underlyings: shouldShow.underlyings,
          coupons: shouldShow.coupons,
          earlyRedemption: shouldShow.earlyRedemption && filteredEarlyRedemption.length > 0,
          performance: shouldShow.performance,
          couponDetails: shouldShow.couponDetails,
        },
        sectionProps: {
          atGlance: {
            ...sections.atGlance.props,
            tradedNotional: showTradedNotional,
            currentNotional: showCurrentNotional,
          },
          barrier: sections.barrier.props,
          underlyings: sections.underlyings.props,
          coupons: sections.coupons.props,
          performance: sections.performance.props,
          couponDetails: sections.couponDetails.props,
          earlyRedemption: sections.earlyRedemption.props,
        },
        sectionTitles: {
          barrier: sections.barrier.title,
          couponDetails: sections.couponDetails.title,
        },
        earlyRedemption: filteredEarlyRedemption,
        priceHistory: pdfPriceHistory,
        underlyingsHistory: pdfUnderlyingsHistory,
      });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleSharePress = async () => {
    if (!product || isSharing) {
      return;
    }

    try {
      setIsSharing(true);
      const shareLink = await apiService.createProductShareLink(product.isin, ownershipScope);
      setShareUrl(shareLink.url);
      setIsShareModalVisible(true);
    } catch (error: unknown) {
      const errorMessage = getShareLinkErrorMessage(error);
      if (!errorMessage) {
        return;
      }

      if (Platform.OS === 'web') {
        window.alert(`Error: ${errorMessage}`);
      } else {
        Alert.alert('Error', errorMessage);
      }
    } finally {
      setIsSharing(false);
    }
  };

  const focusUnderlying = (index: number) => {
    if (!product?.underlyings?.length) return;

    setSelectedUnderlying(index);
    setIsFocused(true);

    const next = new Array(product.underlyings.length).fill(false);
    next[index] = true;
    setVisibleUnderlyings(next);
  };


  if (loading) {
    return (
      <View style={[styles.centerContainer, Platform.OS === 'web' && { padding: 20, margin: 0 }]}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loadingText}>Loading details...</Text>
      </View>
    );
  }

  if (error || !product) {
    return (
      <View style={[styles.centerContainer, Platform.OS === 'web' && { padding: 20, margin: 0 }]}>
        <Text style={styles.errorText}>{error || 'Product not found'}</Text>
        <TouchableOpacity onPress={onBackPress} style={styles.errorButton}>
          <Text style={styles.errorButtonText}>Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const atGlanceUnderlyingsText = (() => {
    if (!product.underlyings?.length) return '-';
    const seen = new Set<string>();
    const names = product.underlyings
      .filter((u) => {
        const key = u.isin || u.name || '';
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((u) => u.name);
    return names.length ? names.join(', ') : '-';
  })();

  return (
    <View style={[styles.container, Platform.OS === 'web' && { padding: 0, margin: 0 }]}>
      <View style={[styles.header, width >= 1024 && styles.headerDesktop]}>
        <TouchableOpacity onPress={onBackPress} style={styles.backIcon}>
          <Text style={styles.backIconText}>‹</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{width >= 1024 ? 'Product details' : product.isin}</Text>
        <FavouriteToggleButton
          isin={product.isin}
          ownershipScope={ownershipScope}
          style={styles.favouriteButton}
        />
        <TouchableOpacity
          style={[styles.shareButton, isSharing && styles.shareButtonDisabled]}
          onPress={handleSharePress}
          disabled={isSharing}
          activeOpacity={0.7}
        >
          {isSharing ? (
            <ActivityIndicator size="small" color={COLORS.primary} />
          ) : (
            <Text style={styles.shareButtonText}>Share</Text>
          )}
        </TouchableOpacity>
      </View>

      <ScrollView
        style={[styles.scrollView, Platform.OS === 'web' && { padding: 0, margin: 0 }]}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <ProductHeader
          name={product.name}
          currency={product.currency}
          bid={product.bid}
          ask={product.ask}
          valor={product.valor}
          isin={product.isin}
          termSheet={product.termSheet}
          onExportPdf={handleExportPdf}
          isExportingPdf={isExportingPdf}
        />

        {width >= 1024 ? (
          <View style={styles.desktopContainer}>
            {shouldShow.atGlance && (
              <ProductAtGlance
                productCode={product.productCode}
                productType={product.productTypeName || product.productType}
                issuer={product.issuer}
                showIssuer={sections.atGlance.props.Issuer !== false}
                underlyings={atGlanceUnderlyingsText}
                issuePrice={product.issuePrice}
                showIssuePrice={sections.atGlance.props.issuePrice !== false}
                tenorMonths={product.tenorMonths}
                coupon={product.coupon}
                denomination={product.denomination}
                currency={product.currency}
                initialFixing={product.initialFixing}
                issueDate={product.issueDate}
                finalFixing={product.finalFixing}
                redemptionDate={product.redemptionDate}
                couponBarrier={product.couponBarrier}
                showCouponBarrier={sections.atGlance.props.couponBarrier}
                couponMemory={product.couponMemory}
                showCouponMemory={sections.atGlance.props.couponMemory}
                showCoupon={sections.atGlance.props.coupon}
                upside={product.upside}
                showUpside={sections.atGlance.props.upside}
                upsideParticipation={product.upsideParticipation}
                showUpsideParticipation={sections.atGlance.props.upsideParticipation}
                downside={product.downside}
                showDownside={sections.atGlance.props.downside}
                downsideParticipation={product.downsideParticipation}
                showDownsideParticipation={sections.atGlance.props.downsideParticipation}
                showSettlementType={sections.atGlance.props.settlementType}
                settlementType={product.settlementTypeName}
                showGrade={sections.atGlance.props.grade}
                grade={product.grade}
                showRecovery={sections.atGlance.props.recovery}
                recovery={product.recovery}
                basketTypeUp={product.basketTypeUp}
                strikeLevel={product.strikeLevel}
                bonusLevel={product.bonusLevel}
                capLevel={product.capLevel}
                maxReturn={product.maxReturn}
                tradedNotional={product.tradedNotional}
                showTradedNotional={showTradedNotional}
                currentNotional={product.currentNotional}
                showCurrentNotional={showCurrentNotional}
                atGlanceProps={sections.atGlance.props}
                showPremium={sections.atGlance.props.premium}
                premium={product.premiumPct}
                showQuotationType={sections.atGlance.props.quotationType}
                quotationType={product.quotationType}
                showStrike={sections.atGlance.props.strike}
                strike={product.strikeLevel}
                showBasketOptionType={sections.atGlance.props.basketOptionType}
                basketOptionType={product.basketOptionTypeName}
                showUpperStrike={sections.atGlance.props.upperStrike}
                upperStrike={product.upperStrikePct}
                showLowerStrike={sections.atGlance.props.lowerStrike}
                showBarrierType={sections.atGlance.props.barrierType}
                barrierType={product.barrierStyleName}
                showLowerBarrier={sections.atGlance.props.lowerBarrier}
                lowerBarrier={product.barrierLevel}
                showUpperBarrier={sections.atGlance.props.upperBarrier}
                upperBarrier={product.barrierUpper}
                showDenomination={sections.atGlance.props.denomination}
                showLeverage={sections.atGlance.props.leverage}
                leverage={product.leverage}
                showRatio={sections.atGlance.props.ratio}
                ratio={product.ratio}
                showStopLoss={sections.atGlance.props.stopLoss}
                stopLoss={product.stopLoss}
                showCouponTrigger={sections.atGlance.props.couponTrigger}
                couponTrigger={product.couponTrigger}
                showCouponFrequency={sections.atGlance.props.couponFrequency}
                couponFrequency={product.couponFrequencyName}
                showCouponType={sections.atGlance.props.couponType}
                couponType={product.couponTypeName}
                showCapitalProtection={sections.atGlance.props.capitalProtectionPct}
                capitalProtection={product.capitalProtectionPct}
                showCouponStepUp={sections.atGlance.props.stepUp}
                couponStepUp={product.couponStepUp}
                showCouponMinPct={sections.atGlance.props.couponMinimum}
                couponMinPct={product.couponMinPct}
                showCouponMaxPct={sections.atGlance.props.couponMaximum}
                couponMaxPct={product.couponMaxPct}
              />
            )}

            {shouldShow.barrier && (
              <ProductBarrierInfo
                barrierStyleName={product.barrierStyleName}
                barrierHitRecorded={product.barrierHitRecorded}
                strikeLevel={product.strikeLevel ?? null}
                barrierLevel={product.barrierLevel}
                underlyings={product.underlyings || []}
                couponMemory={product.couponMemory}
                showCouponMemory={sections.barrier.props.couponMemory}
                showDistanceCouponBarrier={sections.barrier.props.distanceCouponBarrier}
                couponBarrier={product.couponBarrier}
                barrierProps={sections.barrier.props}
                sectionTitle={sections.barrier.title}
                showDistanceStopLoss={sections.barrier.props.distanceStopLoss}
                distanceStopLoss={product.stopLossDistance}
                showStoppLoss={sections.barrier.props.stopLoss}
                stopLoss={product.stopLoss}
              />
            )}

            {shouldShow.couponDetails && (
              <CouponDetails
                coupon={product.coupon}
                couponFrequency={product.couponFrequencyName}
                couponMinPct={product.couponMinPct}
                couponMaxPct={product.couponMaxPct}
                couponType={product.couponTypeName}
                referenceRate={product.referenceRateName}
                couponDetailsProps={sections.couponDetails.props}
              />
            )}

            {shouldShow.performance && (
              <View style={styles.section}>
                <ProductPerformanceChart
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  priceHistory={priceHistory}
                  fullPriceHistory={fullPriceHistory}
                  underlyingsHistory={underlyingsHistory}
                  fullUnderlyingsHistory={fullUnderlyingsHistory}
                  visibleUnderlyings={visibleUnderlyings}
                  loadingHistory={loadingHistory}
                  product={product}
                  calculatePerformance={calculatePerformance}
                  isDesktopCombined={true}
                  selectedUnderlyingIndex={selectedUnderlying}
                  onSelectUnderlyingIndex={(i) => focusUnderlying(i)}
                  onShowAllUnderlyings={showAllUnderlyings}
                  isFocused={isFocused}
                  showUnderlyingsInChart={sections.performance.props.showUnderlyingsInChart !== false}
                  showTooltipPrice={sections.performance.props.showTooltipPrice !== false}
                />
              </View>
            )}

            {shouldShow.underlyings && (
              <ProductUnderlyingsInfo
                underlyings={product.underlyings}
                visibleUnderlyings={visibleUnderlyings}
                currency={product.currency}
                onToggleVisibility={toggleUnderlyingVisibility}
                isDesktopCombined={true}
                showDistanceCouponBarrier={sections.underlyings.props.distanceCouponBarrier}
                showBarrierLevel={sections.underlyings.props.barrierLevel !== false}
                showBarrierDistance={sections.underlyings.props.barrierDistance !== false}
                showAutocallDistance={sections.underlyings.props.autocallDistance === true}
                underlyingsProps={sections.underlyings.props}
                showUpperStrike={sections.underlyings.props.upperStrike}
                showStrikeLevel={sections.underlyings.props.strikeLevel}
                showStrikeDistance={sections.underlyings.props.strikeDistance}
              />
            )}

            <View style={styles.desktopRow}>
              <View style={styles.desktopHalfColumn}>
                {shouldShow.coupons && (
                  <ProductCouponsTable 
                    coupons={product.coupons} 
                    currency={product.currency}
                    showExDate={sections.coupons.props.showExDate !== false}
                  />
                )}
              </View>
              <View style={styles.desktopHalfColumn}>
                {shouldShow.earlyRedemption && earlyRedemption.length > 0 && (() => {
                  // Filtrar items según configuración
                  const filteredItems = earlyRedemption.filter((item) => {
                    if (sections.earlyRedemption.props.redemptionLevel) {
                      // Si muestra redemptionLevel (autocall), mostrar todos los que tengan valor
                      return item.redemptionLevel != null;
                    }
                    // Si no muestra redemptionLevel (callable), mostrar todos
                    return true;
                  });

                  if (filteredItems.length === 0) return null;

                  return (
                  <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Early redemption</Text>
                    <View style={styles.titleSeparator} />
                      {filteredItems.map((item, index) => (
                      <View key={index} style={styles.earlyRedemptionItem}>
                        <View style={styles.earlyRedemptionRow}>
                          {sections.earlyRedemption.props.observationDate !== false && (
                            <View style={styles.earlyRedemptionField}>
                              <Text style={styles.earlyRedemptionLabel}>Observation date</Text>
                              <Text style={styles.earlyRedemptionValue}>
                                {formatDate(item.observationDate)}
                              </Text>
                            </View>
                          )}
                          {sections.earlyRedemption.props.redemptionLevel && (
                            <View style={styles.earlyRedemptionField}>
                              <Text style={styles.earlyRedemptionLabel}>Early redemption level</Text>
                              <Text style={styles.earlyRedemptionValue}>
                                {item.redemptionLevel === 0 || item.redemptionLevel === null
                                  ? '-'
                                  : `${item.redemptionLevel}%`}
                              </Text>
                            </View>
                          )}
                          {sections.earlyRedemption.props.showIssuerCallable && (
                            <View style={styles.earlyRedemptionField}>
                              <Text style={styles.earlyRedemptionLabel}>Early redemption level</Text>
                              <Text style={styles.earlyRedemptionValue}>Issuer Callable</Text>
                            </View>
                          )}
                        </View>
                      </View>
                    ))}
                  </View>
                  );
                })()}
              </View>
            </View>
          </View>
        ) : isXl(width) ? (
          <>
            {shouldShow.atGlance && (
              <ProductAtGlance
                productCode={product.productCode}
                productType={product.productTypeName || product.productType}
                showIssuer={true}
                issuer={product.issuer}
                underlyings={atGlanceUnderlyingsText}
                issuePrice={product.issuePrice}
                tenorMonths={product.tenorMonths}
                coupon={product.coupon}
                denomination={product.denomination}
                currency={product.currency}
                initialFixing={product.initialFixing}
                issueDate={product.issueDate}
                finalFixing={product.finalFixing}
                redemptionDate={product.redemptionDate}
                couponBarrier={product.couponBarrier}
                showCouponBarrier={sections.atGlance.props.couponBarrier}
                couponMemory={product.couponMemory}
                showCouponMemory={sections.atGlance.props.couponMemory}
                showCoupon={sections.atGlance.props.coupon}
                upside={product.upside}
                showUpside={sections.atGlance.props.upside}
                upsideParticipation={product.upsideParticipation}
                showUpsideParticipation={sections.atGlance.props.upsideParticipation}
                downside={product.downside}
                showDownside={sections.atGlance.props.downside}
                downsideParticipation={product.downsideParticipation}
                showDownsideParticipation={sections.atGlance.props.downsideParticipation}
                showSettlementType={sections.atGlance.props.settlementType}
                settlementType={product.settlementTypeName}
                showGrade={sections.atGlance.props.grade}
                grade={product.grade}
                showRecovery={sections.atGlance.props.recovery}
                recovery={product.recovery}
                basketTypeUp={product.basketTypeUp}
                strikeLevel={product.strikeLevel}
                bonusLevel={product.bonusLevel}
                capLevel={product.capLevel}
                maxReturn={product.maxReturn}
                tradedNotional={product.tradedNotional}
                showTradedNotional={showTradedNotional}
                currentNotional={product.currentNotional}
                showCurrentNotional={showCurrentNotional}
                atGlanceProps={sections.atGlance.props}
                showPremium={sections.atGlance.props.premium}
                premium={product.premiumPct}
                showQuotationType={sections.atGlance.props.quotationType}
                quotationType={product.quotationType}
                showStrike={sections.atGlance.props.strike}
                strike={product.strikeLevel}
                showBasketOptionType={sections.atGlance.props.basketOptionType}
                basketOptionType={product.basketOptionTypeName}
                showUpperStrike={sections.atGlance.props.upperStrike}
                upperStrike={product.upperStrikePct}
                showLowerStrike={sections.atGlance.props.lowerStrike}
                showBarrierType={sections.atGlance.props.barrierType}
                barrierType={product.barrierStyleName}
                showLowerBarrier={sections.atGlance.props.lowerBarrier}
                lowerBarrier={product.barrierLevel}
                showUpperBarrier={sections.atGlance.props.upperBarrier}
                upperBarrier={product.barrierUpper}
                showDenomination={sections.atGlance.props.denomination}
                showLeverage={sections.atGlance.props.leverage}
                leverage={product.leverage}
                showRatio={sections.atGlance.props.ratio}
                ratio={product.ratio}
                showStopLoss={sections.atGlance.props.stopLoss}
                stopLoss={product.stopLoss}
                showCouponTrigger={sections.atGlance.props.couponTrigger}
                couponTrigger={product.couponTrigger}
                showCouponFrequency={sections.atGlance.props.couponFrequency}
                couponFrequency={product.couponFrequencyName}
                showCouponType={sections.atGlance.props.couponType}
                couponType={product.couponTypeName}
                showCapitalProtection={sections.atGlance.props.capitalProtectionPct}
                capitalProtection={product.capitalProtectionPct}
                showCouponStepUp={sections.atGlance.props.stepUp}
                couponStepUp={product.couponStepUp}
                showCouponMinPct={sections.atGlance.props.couponMinimum}
                couponMinPct={product.couponMinPct}
                showCouponMaxPct={sections.atGlance.props.couponMaximum}
                couponMaxPct={product.couponMaxPct}
                showIssuePrice={sections.atGlance.props.issuePrice !== false}
              />
            )}

            <View style={styles.tabletRow}>
              <View style={styles.tabletColumn}>
                {shouldShow.barrier && (
                  <ProductBarrierInfo
                    barrierStyleName={product.barrierStyleName}
                    barrierHitRecorded={product.barrierHitRecorded}
                    strikeLevel={product.strikeLevel ?? null}
                    barrierLevel={product.barrierLevel}
                    underlyings={product.underlyings || []}
                    couponMemory={product.couponMemory}
                    showCouponMemory={sections.barrier.props.couponMemory}
                    showDistanceCouponBarrier={sections.barrier.props.distanceCouponBarrier}
                    couponBarrier={product.couponBarrier}
                    barrierProps={sections.barrier.props}
                    sectionTitle={sections.barrier.title}
                    showDistanceStopLoss={sections.barrier.props.distanceStopLoss}
                    distanceStopLoss={product.stopLossDistance}
                    showStoppLoss={sections.barrier.props.stopLoss}
                    stopLoss={product.stopLoss}
                  />
                )}

                {shouldShow.couponDetails && (
                  <CouponDetails
                    coupon={product.coupon}
                    couponFrequency={product.couponFrequencyName}
                    couponMinPct={product.couponMinPct}
                    couponMaxPct={product.couponMaxPct}
                    couponType={product.couponTypeName}
                    referenceRate={product.referenceRateName}
                    couponDetailsProps={sections.couponDetails.props}
                  />
                )}
              </View>
              <View style={styles.tabletColumn}>
                {shouldShow.performance && (
                  <>
                    <ProductPerformanceChart
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      priceHistory={priceHistory}
                      fullPriceHistory={fullPriceHistory}
                      underlyingsHistory={underlyingsHistory}
                      fullUnderlyingsHistory={fullUnderlyingsHistory}
                      visibleUnderlyings={visibleUnderlyings}
                      loadingHistory={loadingHistory}
                      product={product}
                      calculatePerformance={calculatePerformance}
                      selectedUnderlyingIndex={selectedUnderlying}
                      onSelectUnderlyingIndex={(i) => focusUnderlying(i)}
                      onShowAllUnderlyings={showAllUnderlyings}
                      isFocused={isFocused}
                      showUnderlyingsInChart={sections.performance.props.showUnderlyingsInChart !== false}
                    />
                  </>
                )}
              </View>
            </View>

            <View style={styles.tabletRow}>
              <View style={styles.tabletColumn}>
                {shouldShow.coupons && (
                  <ProductCouponsTable 
                    coupons={product.coupons} 
                    currency={product.currency}
                    showExDate={sections.coupons.props.showExDate !== false}
                  />
                )}
              </View>
              <View style={styles.tabletColumn}>
                {shouldShow.underlyings && (
                  <ProductUnderlyingsInfo
                    underlyings={product.underlyings}
                    visibleUnderlyings={visibleUnderlyings}
                    currency={product.currency}
                    onToggleVisibility={toggleUnderlyingVisibility}
                    showDistanceCouponBarrier={sections.underlyings.props.distanceCouponBarrier}
                    showBarrierLevel={sections.underlyings.props.barrierLevel !== false}
                    showBarrierDistance={sections.underlyings.props.barrierDistance !== false}
                    showAutocallDistance={sections.underlyings.props.autocallDistance === true}
                    underlyingsProps={sections.underlyings.props}
                    showUpperStrike={sections.underlyings.props.upperStrike}
                    showStrikeLevel={sections.underlyings.props.strikeLevel}
                    showStrikeDistance={sections.underlyings.props.strikeDistance}
                  />
                )}
              </View>
            </View>
          </>
        ) : (
          <>
            {shouldShow.atGlance && (
              <ProductAtGlance
                productCode={product.productCode}
                productType={product.productTypeName || product.productType}
                issuer={product.issuer}
                underlyings={atGlanceUnderlyingsText}
                issuePrice={product.issuePrice}
                tenorMonths={product.tenorMonths}
                coupon={product.coupon}
                denomination={product.denomination}
                currency={product.currency}
                initialFixing={product.initialFixing}
                issueDate={product.issueDate}
                finalFixing={product.finalFixing}
                redemptionDate={product.redemptionDate}
                couponBarrier={product.couponBarrier}
                showCouponBarrier={sections.atGlance.props.couponBarrier}
                couponMemory={product.couponMemory}
                showCouponMemory={sections.atGlance.props.couponMemory}
                showCoupon={sections.atGlance.props.coupon}
                upside={product.upside}
                showUpside={sections.atGlance.props.upside}
                upsideParticipation={product.upsideParticipation}
                showUpsideParticipation={sections.atGlance.props.upsideParticipation}
                downside={product.downside}
                showDownside={sections.atGlance.props.downside}
                downsideParticipation={product.downsideParticipation}
                showDownsideParticipation={sections.atGlance.props.downsideParticipation}
                showSettlementType={sections.atGlance.props.settlementType}
                settlementType={product.settlementTypeName}
                showGrade={sections.atGlance.props.grade}
                grade={product.grade}
                showRecovery={sections.atGlance.props.recovery}
                recovery={product.recovery}
                basketTypeUp={product.basketTypeUp}
                strikeLevel={product.strikeLevel}
                bonusLevel={product.bonusLevel}
                capLevel={product.capLevel}
                maxReturn={product.maxReturn}
                tradedNotional={product.tradedNotional}
                showTradedNotional={showTradedNotional}
                currentNotional={product.currentNotional}
                showCurrentNotional={showCurrentNotional}
                atGlanceProps={sections.atGlance.props}
                showPremium={sections.atGlance.props.premium}
                premium={product.premiumPct}
                showQuotationType={sections.atGlance.props.quotationType}
                quotationType={product.quotationType}
                showStrike={sections.atGlance.props.strike}
                strike={product.strikeLevel}
                showBasketOptionType={sections.atGlance.props.basketOptionType}
                basketOptionType={product.basketOptionTypeName}
                showUpperStrike={sections.atGlance.props.upperStrike}
                upperStrike={product.upperStrikePct}
                showLowerStrike={sections.atGlance.props.lowerStrike}
                showBarrierType={sections.atGlance.props.barrierType}
                barrierType={product.barrierStyleName}
                showLowerBarrier={sections.atGlance.props.lowerBarrier}
                lowerBarrier={product.barrierLevel}
                showUpperBarrier={sections.atGlance.props.upperBarrier}
                upperBarrier={product.barrierUpper}
                showDenomination={sections.atGlance.props.denomination}
                showLeverage={sections.atGlance.props.leverage}
                leverage={product.leverage}
                showRatio={sections.atGlance.props.ratio}
                ratio={product.ratio}
                showStopLoss={sections.atGlance.props.stopLoss}
                stopLoss={product.stopLoss}
                showCouponTrigger={sections.atGlance.props.couponTrigger}
                couponTrigger={product.couponTrigger}
                showCouponFrequency={sections.atGlance.props.couponFrequency}
                couponFrequency={product.couponFrequencyName}
                showCouponType={sections.atGlance.props.couponType}
                couponType={product.couponTypeName}
                showCapitalProtection={sections.atGlance.props.capitalProtectionPct}
                capitalProtection={product.capitalProtectionPct}
                showCouponStepUp={sections.atGlance.props.stepUp}
                couponStepUp={product.couponStepUp}
                showCouponMinPct={sections.atGlance.props.couponMinimum}
                couponMinPct={product.couponMinPct}
                showCouponMaxPct={sections.atGlance.props.couponMaximum}
                couponMaxPct={product.couponMaxPct}
                showIssuePrice={sections.atGlance.props.issuePrice !== false}
              />
            )}

            {shouldShow.performance && (
              <>
                <ProductPerformanceChart
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  priceHistory={priceHistory}
                  fullPriceHistory={fullPriceHistory}
                  underlyingsHistory={underlyingsHistory}
                  fullUnderlyingsHistory={fullUnderlyingsHistory}
                  visibleUnderlyings={visibleUnderlyings}
                  loadingHistory={loadingHistory}
                  product={product}
                  calculatePerformance={calculatePerformance}
                  selectedUnderlyingIndex={selectedUnderlying}
                  onSelectUnderlyingIndex={(i) => focusUnderlying(i)}
                  onShowAllUnderlyings={showAllUnderlyings}
                  isFocused={isFocused}
                  showUnderlyingsInChart={sections.performance.props.showUnderlyingsInChart !== false}
                />
              </>
            )}

            {shouldShow.underlyings && (
              <ProductUnderlyingsInfo
                underlyings={product.underlyings}
                visibleUnderlyings={visibleUnderlyings}
                currency={product.currency}
                onToggleVisibility={toggleUnderlyingVisibility}
                showDistanceCouponBarrier={sections.underlyings.props.distanceCouponBarrier}
                showBarrierLevel={sections.underlyings.props.barrierLevel !== false}
                showBarrierDistance={sections.underlyings.props.barrierDistance !== false}
                showAutocallDistance={sections.underlyings.props.autocallDistance === true}
                underlyingsProps={sections.underlyings.props}
                showUpperStrike={sections.underlyings.props.upperStrike}
                showStrikeLevel={sections.underlyings.props.strikeLevel}
                showStrikeDistance={sections.underlyings.props.strikeDistance}
              />
            )}

            {shouldShow.barrier && (
              <ProductBarrierInfo
                barrierStyleName={product.barrierStyleName}
                barrierHitRecorded={product.barrierHitRecorded}
                strikeLevel={product.strikeLevel ?? null}
                barrierLevel={product.barrierLevel}
                underlyings={product.underlyings || []}
                couponMemory={product.couponMemory}
                showCouponMemory={sections.barrier.props.couponMemory}
                showDistanceCouponBarrier={sections.barrier.props.distanceCouponBarrier}
                couponBarrier={product.couponBarrier}
                barrierProps={sections.barrier.props}
                sectionTitle={sections.barrier.title}
                showDistanceStopLoss={sections.barrier.props.distanceStopLoss}
                distanceStopLoss={product.stopLossDistance}
                showStoppLoss={sections.barrier.props.stopLoss}
                stopLoss={product.stopLoss}
              />
            )}

            {shouldShow.couponDetails && (
              <CouponDetails
                coupon={product.coupon}
                couponFrequency={product.couponFrequencyName}
                couponMinPct={product.couponMinPct}
                couponMaxPct={product.couponMaxPct}
                couponType={product.couponTypeName}
                referenceRate={product.referenceRateName}
                couponDetailsProps={sections.couponDetails.props}
              />
            )}

            {shouldShow.coupons && (
              <ProductCouponsTable 
                coupons={product.coupons} 
                currency={product.currency}
                showExDate={sections.coupons.props.showExDate !== false}
              />
            )}
          </>
        )}

        {width < 1024 && shouldShow.earlyRedemption && earlyRedemption.length > 0 && (() => {
          // Filtrar items según configuración
          const filteredItems = earlyRedemption.filter((item) => {
            if (sections.earlyRedemption.props.redemptionLevel) {
              // Si muestra redemptionLevel (autocall), mostrar todos los que tengan valor
              return item.redemptionLevel != null;
            }
            // Si no muestra redemptionLevel (callable), mostrar todos
            return true;
          });

          if (filteredItems.length === 0) return null;

          return (
          <View
            style={[styles.section, Platform.OS === 'web' && { padding: 16, marginBottom: 12 }]}
          >
            <Text style={styles.sectionTitle}>Early redemption</Text>
            <View style={styles.titleSeparator} />
            {filteredItems.map((item, index) => (
              <View key={index} style={styles.earlyRedemptionItem}>
                <View style={styles.earlyRedemptionRow}>
                  {sections.earlyRedemption.props.observationDate !== false && (
                    <View style={styles.earlyRedemptionField}>
                      <Text style={styles.earlyRedemptionLabel}>Observation date</Text>
                      <Text style={styles.earlyRedemptionValue}>{formatDate(item.observationDate)}</Text>
                    </View>
                  )}
                  {sections.earlyRedemption.props.redemptionLevel && (
                    <View style={styles.earlyRedemptionField}>
                      <Text style={styles.earlyRedemptionLabel}>Early redemption level</Text>
                      <Text style={styles.earlyRedemptionValue}>
                        {item.redemptionLevel === 0 || item.redemptionLevel === null
                          ? '-'
                          : `${item.redemptionLevel}%`}
                      </Text>
                    </View>
                  )}
                  {sections.earlyRedemption.props.showIssuerCallable && (
                    <View style={styles.earlyRedemptionField}>
                      <Text style={styles.earlyRedemptionLabel}>Early redemption level</Text>
                      <Text style={styles.earlyRedemptionValue}>Issuer Callable</Text>
                    </View>
                  )}
                </View>
              </View>
            ))}
          </View>
          );
        })()}

        <View style={{ height: 60 }} />
      </ScrollView>

      <BottomNavigation
        onNavigateToProducts={onNavigateToProducts}
        onNavigateToHome={onNavigateToHome}
        activeTab="portfolio"
      />

      <ProductShareModal
        visible={isShareModalVisible}
        shareUrl={shareUrl}
        onClose={() => setIsShareModalVisible(false)}
      />
    </View>
  );
};
